import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  ReliabilityScoreInfluence,
  ReliabilityScoreInfluenceDirection,
  User,
} from '@core/generated/gql-types';
import { reliabilityScoreActions } from '@core/reliabilityScore/reliabilityScore.action';
import { formatReliabilityScore } from '@core/reliabilityScore/reliabilityScore.util';
import { useClassificationStore } from '@core/classification/useClassificationStore';
import { useClientStore } from '@core/client/useClientStore';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import { actionPrimaryButtonClassName } from '@core/theme/actionTheme.util';
import EditableSection from '@/components/shared/EditableSection';
import ExemptionConfirmModal from './ExemptionConfirmModal';
import {
  formatReliabilityDate,
  isCancelledDirection,
  mapReliabilityInfluencesToRows,
} from '@/modules/workers/utils/reliability.utils';

interface CandidateReliabilityProps {
  worker: User;
}

const LISTED_DIRECTIONS = [
  ReliabilityScoreInfluenceDirection.Negative,
  ReliabilityScoreInfluenceDirection.Excluded,
];

const COLUMN_HEADINGS = [
  'Date',
  'Client',
  'Skill set',
  'Industry',
  'Posted date',
];

const CandidateReliability: React.FC<CandidateReliabilityProps> = ({
  worker,
}) => {
  const [influences, setInfluences] = useState<ReliabilityScoreInfluence[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [updatingJobId, setUpdatingJobId] = useState<string | null>(null);
  const [pendingExemption, setPendingExemption] = useState<{
    jobId: string;
    direction: ReliabilityScoreInfluenceDirection;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const {
    classificationGroups,
    fetchClassificationGroups,
    getClassificationById,
  } = useClassificationStore();
  const clients = useClientStore((state) => state.clients);
  const fetchDetailedWorkerById = useWorkerStore(
    (state) => state.fetchDetailedWorkerById,
  );

  useEffect(() => {
    let isCancelled = false;

    const fetchInfluences = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const result =
          await reliabilityScoreActions.fetchJobsContributingToReliabilityScore(
            worker.id,
            LISTED_DIRECTIONS,
          );
        if (!isCancelled) {
          setInfluences(result);
        }
      } catch (fetchError) {
        if (!isCancelled) {
          setInfluences([]);
          setError(
            fetchError instanceof Error
              ? fetchError.message
              : 'Failed to load reliability data',
          );
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    void fetchInfluences();

    return () => {
      isCancelled = true;
    };
  }, [worker.id]);

  useEffect(() => {
    if (worker.tenantId && classificationGroups.length === 0) {
      void fetchClassificationGroups(worker.tenantId);
    }
  }, [classificationGroups.length, fetchClassificationGroups, worker.tenantId]);

  const rows = useMemo(
    () =>
      mapReliabilityInfluencesToRows(influences, {
        getClientName: (clientId) =>
          clients.find((client) => client.id === clientId)?.name ?? undefined,
        getClassification: getClassificationById,
        getIndustryName: (classificationGroupId) =>
          classificationGroups.find(
            (group) => group.id === classificationGroupId,
          )?.name ?? undefined,
      }),
    [classificationGroups, clients, getClassificationById, influences],
  );

  const reliability = formatReliabilityScore(worker.reliabilityScore);

  const cancelledCount = useMemo(() => {
    const negative = rows.filter(
      (row) => row.direction === ReliabilityScoreInfluenceDirection.Negative,
    ).length;
    const total = rows.filter((row) =>
      isCancelledDirection(row.direction),
    ).length;

    return { negative, total };
  }, [rows]);

  const handleExemptionChange = async (
    jobId: string,
    direction: ReliabilityScoreInfluenceDirection,
  ) => {
    const isUndo = direction === ReliabilityScoreInfluenceDirection.Excluded;
    const canToggle =
      isUndo || direction === ReliabilityScoreInfluenceDirection.Negative;
    if (!canToggle) return;

    setUpdatingJobId(jobId);

    try {
      await reliabilityScoreActions.convertJobInfluenceDirection(
        jobId,
        worker.id,
      );

      const [refreshedInfluences] = await Promise.all([
        reliabilityScoreActions.fetchJobsContributingToReliabilityScore(
          worker.id,
          LISTED_DIRECTIONS,
        ),
        fetchDetailedWorkerById(worker.id),
      ]);
      setInfluences(refreshedInfluences);
      setPendingExemption(null);
      toast.success(isUndo ? 'Job exemption removed' : 'Job exempted');
    } catch (mutationError) {
      toast.error(
        mutationError instanceof Error
          ? mutationError.message
          : 'Failed to update job exemption',
      );
    } finally {
      setUpdatingJobId(null);
    }
  };

  return (
    <EditableSection
      title={reliability ? `Reliability: ${reliability}` : 'Reliability'}
      collapsible
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Reliability is determined by the number of cancelled jobs vs total
          accepted jobs.
        </p>
        <p className="text-sm text-gray-600">
          Jobs that were cancelled for appropriate reasons can be exempted from
          the candidate&apos;s reliability rating.
        </p>

        <h3 className="pt-2 text-base font-semibold text-gray-900">
          Cancelled jobs
          {cancelledCount.total > 0
            ? ` (${cancelledCount.negative}/${cancelledCount.total})`
            : ''}
        </h3>

        {isLoading ? (
          <p className="text-sm text-gray-500">Loading reliability data...</p>
        ) : error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-500">
            This candidate has no cancelled jobs.
          </p>
        ) : (
          <div className="max-h-[345px] overflow-auto rounded-lg border border-gray-200">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 z-10 bg-white shadow-[inset_0_-1px_0_0_#e5e7eb]">
                <tr>
                  {COLUMN_HEADINGS.map((heading) => (
                    <th
                      key={heading}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500"
                    >
                      {heading}
                    </th>
                  ))}
                  <th className="px-4 py-3">
                    <span className="sr-only">Exemption</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const isExempt =
                    row.direction ===
                    ReliabilityScoreInfluenceDirection.Excluded;
                  const canChangeExemption =
                    isExempt ||
                    row.direction ===
                      ReliabilityScoreInfluenceDirection.Negative;
                  const isUpdating = updatingJobId === row.jobId;

                  return (
                    <tr
                      key={row.id}
                      className="border-b border-gray-100 last:border-b-0"
                    >
                      <td className="px-4 py-3 text-sm whitespace-nowrap text-gray-900">
                        {formatReliabilityDate(row.date)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {row.clientName || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center rounded-md bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                          {row.skillSet || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {row.industry || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap text-gray-900">
                        {formatReliabilityDate(row.postedDate)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {canChangeExemption ? (
                          <button
                            type="button"
                            disabled={isUpdating}
                            onClick={() =>
                              setPendingExemption({
                                jobId: row.jobId,
                                direction: row.direction,
                              })
                            }
                            className={`w-40 rounded-md px-3 py-2 text-sm font-medium ${
                              isExempt
                                ? 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                                : actionPrimaryButtonClassName
                            } ${isUpdating ? 'cursor-not-allowed opacity-60' : ''}`}
                          >
                            {isUpdating
                              ? 'Updating...'
                              : isExempt
                                ? 'Undo Exemption'
                                : 'Exempt Job'}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <ExemptionConfirmModal
        isOpen={pendingExemption !== null}
        isUndo={
          pendingExemption?.direction ===
          ReliabilityScoreInfluenceDirection.Excluded
        }
        isUpdating={updatingJobId !== null}
        onClose={() => {
          if (updatingJobId === null) {
            setPendingExemption(null);
          }
        }}
        onConfirm={() => {
          if (!pendingExemption) return;
          void handleExemptionChange(
            pendingExemption.jobId,
            pendingExemption.direction,
          );
        }}
      />
    </EditableSection>
  );
};

export default CandidateReliability;
